<!-- Component can be used in microscope and periscope -->
<template>
  <fieldset ref="root" class="country-id-list" :disabled="disabled">
    <legend class="legend">{{ label }}</legend>

    <ul class="rows">
      <li v-for="row in rows" :key="row.countryCode" class="row" :data-primary="row.isPrimary ? 'true' : 'false'">
        <span class="country">{{ borgoCountryName(row.countryCode) }}</span>
        <AppInput
          class="id-number"
          :label="idLabel"
          :model-value="row.idNumber"
          :disabled="disabled"
          :rules="[idNumberRule(idLabel, row.countryCode)]"
          @input="setIdNumber(row.countryCode, $event)"
        />
        <label class="primary">
          <input
            type="radio"
            :name="primaryGroupName"
            :checked="row.isPrimary"
            :disabled="disabled"
            @change="commit(setPrimaryCountry(rows, row.countryCode))"
          />
          Primärt
        </label>
        <AppButton
          class="remove"
          type="button"
          small
          color="transparent"
          :disabled="disabled || rows.length <= MIN_COUNTRIES"
          :title="rows.length <= MIN_COUNTRIES ? LAST_COUNTRY_MESSAGE : undefined"
          @click="remove(row.countryCode)"
        >
          Ta bort
        </AppButton>
      </li>
    </ul>

    <AppAutoComplete
      :key="pickerKey"
      class="picker"
      :label="addLabel"
      :items="pickableCountries"
      placeholder="Sök land"
      :disabled="disabled"
      hide-details
      @input="pick"
    />

    <p v-if="message" class="message" role="alert">{{ message }}</p>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { BORGO_COUNTRIES, borgoCountryName } from '@staff/shared'

import { useInputValidation } from '../../composables'
import AppAutoComplete from '../AppAutoComplete/AppAutoComplete.vue'
import AppButton from '../AppButton/AppButton.vue'
import AppInput from '../AppInput/AppInput.vue'
import type { CitizenshipEntry, CountryIdListVariant, TaxDomicileEntry } from './CountryIdList.types'
import {
  LAST_COUNTRY_MESSAGE,
  MIN_COUNTRIES,
  addCountry,
  fromCountryIdRows,
  idNumberRule,
  removeCountry,
  setCountryIdNumber,
  setPrimaryCountry,
  toCountryIdRows,
  validateCountryIdRows,
  type CountryIdRow,
  type CountryIdRowsChange,
} from './countryIdList'

const props = defineProps<{
  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  value?: CitizenshipEntry[] | TaxDomicileEntry[]
  modelValue?: CitizenshipEntry[] | TaxDomicileEntry[]
  variant: CountryIdListVariant
  label: string
  /** Names the ID number of a row, e.g. "TIN". */
  idLabel: string
  addLabel: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'input', value: CitizenshipEntry[] | TaxDomicileEntry[]): void
  (e: 'update:modelValue', value: CitizenshipEntry[] | TaxDomicileEntry[]): void
}>()

const root = ref<HTMLElement | null>(null)
const rows = computed(() => toCountryIdRows(props.variant, props.modelValue ?? props.value ?? []))

const primaryGroupName = `country-id-primary-${Math.random().toString(36).slice(2)}`

// A refused pick or removal is reported until the next change.
const refusedMessage = ref('')

const { errorMessages } = useInputValidation(
  () => rows.value,
  () => [{ id: 'countryIdRows', rule: (value: unknown) => validateCountryIdRows(value as CountryIdRow[]) }],
  root,
)

const message = computed(() => refusedMessage.value || errorMessages.value[0] || '')

const pickableCountries = computed(() =>
  BORGO_COUNTRIES.filter((country) => !rows.value.some((row) => row.countryCode === country.code)).map((country) => ({
    text: country.name,
    value: country.code,
  })),
)

// Remounting the picker clears it after each pick.
const pickerKey = ref(0)

const commit = (next: CountryIdRow[]) => {
  refusedMessage.value = ''
  const entries = fromCountryIdRows(props.variant, next)
  emit('input', entries)
  emit('update:modelValue', entries)
}

const apply = (change: CountryIdRowsChange) => {
  if (change.refused !== undefined) {
    refusedMessage.value = change.refused
  } else {
    commit(change.rows)
  }
}

const pick = (countryCode: unknown) => {
  if (typeof countryCode !== 'string' || !countryCode) return
  apply(addCountry(rows.value, countryCode))
  pickerKey.value += 1
}

const remove = (countryCode: string) => apply(removeCountry(rows.value, countryCode))

const setIdNumber = (countryCode: string, idNumber: string) =>
  commit(setCountryIdNumber(rows.value, countryCode, idNumber))
</script>

<style lang="scss" scoped>
.country-id-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
}

.legend {
  padding: 0;
  margin-bottom: var(--space-8);
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  font-weight: var(--font-weight-semibold);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  margin: 0;
  padding: 0;
  list-style: none;
  container-type: inline-size;
}

// Narrow list (e.g. two lists side by side): country, primary and remove on one line, the ID number
// below. One line per country once all four fit.
.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas:
    'country primary remove'
    'id id id';
  align-items: center;
  gap: var(--space-8) var(--space-12);
  padding: var(--space-8) var(--space-12);
  border: 1px solid var(--color-border-muted);
  border-radius: var(--radius-md);
  background-color: var(--color-canvas-default);

  &[data-primary='true'] {
    border-color: var(--color-border-default);
    background-color: var(--color-canvas-subtle);
  }
}

@container (min-width: 34rem) {
  .row {
    grid-template-columns: minmax(8rem, 12rem) minmax(10rem, 1fr) auto auto;
    grid-template-areas: 'country id primary remove';
  }
}

.country {
  grid-area: country;
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
}

.id-number {
  grid-area: id;
}

.primary {
  grid-area: primary;
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.remove {
  grid-area: remove;
  white-space: nowrap;
}

.message {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--font-size-sm-line-height);
  color: var(--color-danger-fg);
}
</style>
